import { motion } from 'framer-motion';
import { Home, Building, BookOpen, ArrowRight, ExternalLink } from 'lucide-react';

export default function DoneGroup() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const services = [
    {
      icon: Home,
      title: "Done Mortgage",
      description: "Tailored residential solutions for home or investment mortgages",
      link: "https://donemortgage.ca",
      cta: "Explore Mortgage Options",
      external: true
    },
    {
      icon: Building,
      title: "DK Malik Real Estate",
      description: "Connecting you with the right properties and opportunities",
      link: "https://www.dkmalik.ca/",
      cta: "View Properties", 
      external: true
    },
    {
      icon: BookOpen,
      title: "Blog & Resources",
      description: "Expert insights on property and financial decisions",
      link: "https://donemortgage.ca/blogs/",
      cta: "Read Our Blog",
      external: true
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-emerald-50 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-60"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-50 rounded-full translate-x-1/2 translate-y-1/2 opacity-60"></div>

      <motion.div 
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Part of the <span className="text-emerald-500">Done Group</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            A collection of experts helping Canadians grow through smart property and financial decisions
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} className="grid md:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-emerald-300 flex flex-col h-full"
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  {service.title}
                </h3>
                
                <p className="text-slate-600 mb-6 leading-relaxed flex-grow">
                  {service.description}
                </p>

                <motion.a
                  href={service.link}
                  target={service.external ? "_blank" : "_self"}
                  rel={service.external ? "noopener noreferrer" : ""}
                  className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700 transition-colors group/btn mt-auto"
                  whileHover={{ x: 5 }}
                >
                  {service.cta}
                  {service.external ? (
                    <ExternalLink className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                  ) : (
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  )}
                </motion.a>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Development Advisory Section */}
        <motion.div 
          variants={itemVariants}
          className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-6 md:p-12 text-white mb-12"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Development Advisory
              </h3>
              <p className="text-xl text-emerald-400 mb-6 font-semibold">
                From Blueprint to Buyer
              </p>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Once your project is financed, you'll need the right partner to market and sell it. 
                DK Malik Real Estate specializes in pre-construction marketing and buyer acquisition — 
                helping your developments reach qualified investors and homeowners fast.
              </p>
              
              {/* FIXED BUTTONS - Mobile Responsive */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full">
                <motion.a
                  href="https://www.dkmalik.ca/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 md:px-6 md:py-4 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 transition-colors text-center w-full sm:w-auto"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="text-sm md:text-base">Development Services</span>
                  <ExternalLink className="w-4 h-4 flex-shrink-0" />
                </motion.a>
                
                <motion.a
                  href="#contact"
                  className="flex items-center justify-center gap-2 px-4 py-3 md:px-6 md:py-4 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-colors backdrop-blur-sm border border-white/20 text-center w-full sm:w-auto"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="text-sm md:text-base">Schedule Consultation</span>
                  <ArrowRight className="w-4 h-4 flex-shrink-0" />
                </motion.a>
              </div>
            </div>

            <div className="lg:pl-8 lg:border-l lg:border-slate-700">
              <h4 className="text-xl font-semibold mb-6 text-emerald-400">
                Our Expertise
              </h4>
              <div className="space-y-4">
                {[
                  "Pre-construction marketing",
                  "Buyer acquisition", 
                  "Investor networking",
                  "Market analysis",
                  "Sales optimization",
                  "Digital marketing"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0"></div>
                    <span className="text-slate-300 text-sm md:text-base">{item}</span>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-8 grid grid-cols-2 gap-4 pt-6 border-t border-slate-700">
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-400">100+</div>
                  <div className="text-slate-400 text-sm">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-400">$500M+</div>
                  <div className="text-slate-400 text-sm">Property Value Sold</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div 
          variants={itemVariants}
          className="text-center"
        >
          <p className="text-slate-600 mb-6 text-lg max-w-2xl mx-auto">
            From corporate financing to private lending and mortgage optimization — 
            we make it easy to move forward with confidence.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.a
              href="#contact"
              className="flex items-center justify-center gap-2 px-6 py-3 md:px-8 md:py-4 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 transition-colors shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="text-sm md:text-base">Get Started Today</span>
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </motion.a>
            
            <motion.a
              href="#contact"
              className="flex items-center justify-center gap-2 px-6 py-3 md:px-8 md:py-4 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-700 transition-colors w-full sm:w-auto"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="text-sm md:text-base">Contact Us</span>
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </motion.a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}